import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { clipboardService, adminService } from '../services/api';
import ClipboardItem from './ClipboardItem';
import LinkPreview from './LinkPreview';
import {
  Search,
  Filter,
  RefreshCw,
  Loader2,
  FileText,
  LogOut,
  Settings,
  Grid,
  List,
  Heart,
  Eye,
  Share2,
  Bookmark,
  Sparkles,
  Clock,
  TrendingUp,
  Hash,
  Tag,
  FolderOpen,
  Layers,
  Zap,
  Star,
  Users,
  Calendar,
  MapPin,
  Plus,
  MoreHorizontal,
  UserPlus,
  MessageCircle,
  Award,
  TrendingDown,
  Code,
  Palette
} from 'lucide-react';
import toast from 'react-hot-toast';

const PublicPage5 = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [filters, setFilters] = useState({
    content_type: '',
    sort_by: 'latest'
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 20,
  });

  // Category editing state
  const [editingItem, setEditingItem] = useState(null);
  const [editCategory, setEditCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // New group creation form state
  const [newGroup, setNewGroup] = useState({
    name: '',
    description: '',
    color: 'bg-blue-500'
  });

  // Modal ref for focus management
  const modalRef = useRef(null);

  const fetchPublicItems = async (page = 1, search = searchTerm, filterParams = filters) => {
    try {
      const params = {
        page,
        limit: pagination.items_per_page,
      };
      
      // Only include content_type if it has a value
      if (filterParams.content_type && filterParams.content_type.trim() !== '') {
        params.content_type = filterParams.content_type;
      }
      
      if (search) {
        params.search = search;
      }

      const response = await clipboardService.getPublicItems(params);
      
      // Use the real category field from the API
      const itemsWithGroups = (response.items || []).map(item => ({
        ...item,
        group: item.category_name || item.category || 'Uncategorized'
      }));
      
      setItems(itemsWithGroups);
      setPagination(response.pagination);

      // Update category item counts
      updateCategoryCounts(itemsWithGroups);
    } catch (error) {
      console.error('Error fetching public items:', error);
      toast.error('Failed to load public items');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleEditCategory = (item) => {
    setEditingItem(item.id);
    setEditCategory(item.category_id || '');
  };

  const handleSaveCategory = async (itemId) => {
    try {
      // Call the API to update the category
      const response = await clipboardService.updateItem(itemId, {
        category_id: editCategory
      });
      
      // Find the selected category details
      const selectedCategory = categories.find(cat => cat.id.toString() === editCategory.toString());
      
      // Update local state with the response
      setItems(prevItems =>
        prevItems.map(item =>
          item.id === itemId
            ? {
                ...item,
                category_id: editCategory,
                category_name: selectedCategory?.name || '',
                category_description: selectedCategory?.description || '',
                category_color: selectedCategory?.color || '',
                group: selectedCategory?.name || 'Uncategorized'
              }
            : item
        )
      );
      
      setEditingItem(null);
      setEditCategory('');
      toast.success('Category updated successfully!');
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('Failed to update category');
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditCategory('');
  };

  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await clipboardService.getCategories();
      setCategories(response.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchCategoriesForSidebar = async () => {
    try {
      setCategoriesLoading(true);
      const response = await clipboardService.getCategories();
      
      // Transform API data to match our expected format
      const transformedCategories = (response.categories || []).map(category => {
        const icon = getCategoryIcon(category.name || '');
        return {
          id: category.id || category.name?.toLowerCase().replace(/\s+/g, '-'),
          name: category.name || 'Unnamed Category',
          description: category.description || 'No description available',
          icon: icon || FolderOpen, // Ensure we always have a valid icon
          color: getCategoryColor(category.id || category.name || ''),
          member_count: category.member_count || 0,
          item_count: category.item_count || 0,
          is_default: category.is_default || false,
          created_by: category.created_by || 'Unknown',
          created_at: category.created_at || new Date().toISOString().split('T')[0]
        };
      });
      
      // Add "All Content" category at the beginning
      const allCategories = [
        { 
          id: 'all', 
          name: 'All Content', 
          description: 'Everything shared by the community',
          icon: Layers, 
          color: 'bg-gray-500', 
          member_count: 0,
          item_count: 0,
          is_default: true
        },
        ...transformedCategories
      ];
      
      setCategories(allCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
      
      // Fallback to default categories if API fails
      setCategories([
        { 
          id: 'all', 
          name: 'All Content', 
          description: 'Everything shared by the community',
          icon: Layers, 
          color: 'bg-gray-500', 
          member_count: 0,
          item_count: 0,
          is_default: true
        }
      ]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const getCategoryIcon = (categoryName) => {
    if (!categoryName) return FolderOpen;
    const name = categoryName.toLowerCase();
    if (name.includes('tech') || name.includes('tips')) return Sparkles;
    if (name.includes('web') || name.includes('dev')) return Code;
    if (name.includes('design')) return Palette;
    if (name.includes('productivity')) return Zap;
    if (name.includes('news')) return TrendingUp;
    return FolderOpen;
  };

  const getCategoryColor = (categoryId) => {
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 
      'bg-orange-500', 'bg-red-500', 'bg-pink-500',
      'bg-indigo-500', 'bg-teal-500', 'bg-yellow-500'
    ];
    const index = categoryId.toString().length % colors.length;
    return colors[index];
  };

  const updateCategoryCounts = (items) => {
    setCategories(prev => prev.map(category => {
      if (category.id === 'all') {
        return {
          ...category,
          item_count: items.length
        };
      }
      
      // Count items that belong to this category
      const categoryItemCount = items.filter(item => {
        const itemCategoryId = item.category_id || item.category;
        const itemCategoryName = item.category_name || item.category;
        
        // Match by ID or name
        return itemCategoryId === category.id || 
               itemCategoryName === category.name ||
               (itemCategoryId && itemCategoryId.toString() === category.id.toString()) ||
               (itemCategoryName && itemCategoryName.toLowerCase() === category.name.toLowerCase());
      }).length;
      
      return {
        ...category,
        item_count: categoryItemCount
      };
    }));
  };

  useEffect(() => {
    fetchPublicItems();
    fetchCategories(); // Fetch categories for editing
    fetchCategoriesForSidebar(); // Fetch categories for sidebar
  }, []);

  // Focus management and keyboard/click handlers for modal
  useEffect(() => {
    if (showCreateGroup) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      
      // Focus the modal container and then the first input
      const timer = setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.focus();
          const firstInput = modalRef.current.querySelector('input[type="text"]');
          if (firstInput) {
            firstInput.focus();
          }
        }
      }, 100);

      // Handle ESC key press
      const handleEscKey = (event) => {
        if (event.key === 'Escape') {
          setShowCreateGroup(false);
        }
      };

      // Add event listeners
      document.addEventListener('keydown', handleEscKey);
      
      return () => {
        clearTimeout(timer);
        document.body.style.overflow = 'unset';
        document.removeEventListener('keydown', handleEscKey);
      };
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [showCreateGroup]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPublicItems(1, searchTerm, filters);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    fetchPublicItems(1, searchTerm, newFilters);
  };

  const handlePageChange = (page) => {
    fetchPublicItems(page, searchTerm, filters);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchPublicItems(pagination.current_page, searchTerm, filters);
  };

  const handleCategorySelect = (categoryId) => {
    if (categoryId === 'all') {
      setSelectedGroup(categoryId);
      // Reset to first page when changing categories
      setPagination(prev => ({ ...prev, current_page: 1 }));
    } else {
      // Navigate to category-specific page
      const category = categories.find(cat => cat.id === categoryId);
      if (category) {
        navigate(`/public/category/${encodeURIComponent(category.name)}`);
      }
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!newGroup.name.trim() || !newGroup.description.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      // Check if user is admin
      if (!user?.is_admin) {
        toast.error('Only administrators can create categories. Please contact an admin to create a new category.');
        setShowCreateGroup(false);
        setNewGroup({ name: '', description: '', color: 'bg-blue-500' });
        return;
      }

      // Convert Tailwind color classes to hex colors
      const colorMap = {
        'bg-blue-500': '#3B82F6',
        'bg-green-500': '#10B981',
        'bg-purple-500': '#8B5CF6',
        'bg-orange-500': '#F59E0B',
        'bg-red-500': '#EF4444',
        'bg-pink-500': '#EC4899'
      };

      // Prepare category data for API
      const categoryData = {
        name: newGroup.name.trim(),
        description: newGroup.description.trim(),
        color: colorMap[newGroup.color] || '#3B82F6', // Default to blue if not found
        is_public: true,
        auto_approve_join: true
      };

      // Call the API to create the category
      const response = await clipboardService.createCategory(categoryData);
      
      // Add the new category to the local state
      const iconComponent = getCategoryIcon(response.category.name);
      const newCategoryData = {
        id: response.category.id,
        name: response.category.name,
        description: response.category.description,
        icon: iconComponent || FolderOpen, // Ensure we always have a valid icon
        color: newGroup.color, // Use the original Tailwind color class
        member_count: 0,
        item_count: 0,
        is_default: false,
        created_by: user?.full_name || 'Anonymous',
        created_at: response.category.created_at
      };

      setCategories(prev => [...prev, newCategoryData]);
      setNewGroup({ name: '', description: '', color: 'bg-blue-500' });
      setShowCreateGroup(false);
      toast.success(`Category "${newGroup.name}" created successfully!`);
    } catch (error) {
      console.error('Error creating category:', error);
      
      if (error.response?.status === 403) {
        toast.error('Only administrators can create categories. Please contact an admin to create a new category.');
      } else if (error.response?.status === 409) {
        toast.error('A category with this name already exists. Please choose a different name.');
      } else {
        toast.error('Failed to create category. Please try again.');
      }
    }
  };

  const handleJoinCategory = (categoryId) => {
    setCategories(prev => prev.map(category => 
      category.id === categoryId 
        ? { ...category, member_count: category.member_count + 1 }
        : category
    ));
    toast.success('Joined category successfully!');
  };

  const getContentTypeColor = (contentType) => {
    const colorMap = {
      text: 'bg-blue-100 text-blue-800',
      html: 'bg-green-100 text-green-800',
      url: 'bg-purple-100 text-purple-800',
      image: 'bg-pink-100 text-pink-800',
      code: 'bg-orange-100 text-orange-800',
      document: 'bg-indigo-100 text-indigo-800'
    };
    return colorMap[contentType] || 'bg-gray-100 text-gray-800';
  };

  const filteredItems = items; // Always show all items on the main public page

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-indigo-600" />
            <p className="text-gray-600">Loading content...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-8">
              <Link to="/" className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">BigClipboard</h1>
                  <p className="text-xs text-gray-500">Public Community</p>
                </div>
              </Link>
              
              <nav className="hidden md:flex space-x-8">
                <Link to="/dashboard" className="text-gray-600 hover:text-indigo-600 transition-colors">
                  My Clipboard
                </Link>
                {(user?.is_admin === true || user?.is_admin === "true" || user?.is_admin === 1) && (
                  <Link to="/admin" className="text-red-600 hover:text-red-700 transition-colors">
                    Admin
                  </Link>
                )}
              </nav>
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={() => window.location.href = '/logout'}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar - Categories */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Categories</h2>
                {(user?.is_admin === true || user?.is_admin === "true" || user?.is_admin === 1) && (
                  <button
                    onClick={() => setShowCreateGroup(true)}
                    className="flex items-center space-x-1 px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Create</span>
                  </button>
                )}
              </div>

              {/* Categories List */}
              <div className="space-y-3">
                {categoriesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                    <span className="ml-2 text-gray-600">Loading categories...</span>
                  </div>
                ) : categories.length === 0 ? (
                  <div className="text-center py-8">
                    <FolderOpen className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm text-gray-500">No categories available</p>
                  </div>
                ) : (
                  categories.map((category) => {
                    const IconComponent = category.icon || FolderOpen;
                    return (
                      <button
                        key={category.id}
                        onClick={() => handleCategorySelect(category.id)}
                        className={`w-full text-left p-3 rounded-lg transition-all duration-200 ${
                          selectedGroup === category.id
                            ? 'bg-indigo-50 border-2 border-indigo-200'
                            : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`w-8 h-8 ${category.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                            <IconComponent className="w-4 h-4 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-gray-900 truncate">
                              {category.name}
                            </h3>
                            <div className="flex items-center space-x-2 text-xs text-gray-500">
                              <Users className="w-3 h-3" />
                              <span>{category.member_count}</span>
                              <span>•</span>
                              <span>{category.item_count} items</span>
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>

              {/* Create Category Modal */}
              {showCreateGroup && (
                <>
                  {/* Backdrop to prevent background interaction */}
                  <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-[99999]" />
                  <div className="fixed inset-0 flex items-center justify-center z-[100000]" onClick={() => setShowCreateGroup(false)}>
                    <div ref={modalRef} className="bg-white rounded-xl p-6 w-full max-w-md mx-4 relative z-[100001]" onClick={(e) => e.stopPropagation()} tabIndex={-1} autoFocus>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Category</h3>
                    <form onSubmit={handleCreateCategory} className="space-y-4">
                                              <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Category Name
                          </label>
                        <input
                          type="text"
                          value={newGroup.name}
                          onChange={(e) => setNewGroup(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="e.g., Tech Tips"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <textarea
                          value={newGroup.description}
                          onChange={(e) => setNewGroup(prev => ({ ...prev, description: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          rows={3}
                          placeholder="Describe what this group is about..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Color Theme
                        </label>
                        <select
                          value={newGroup.color}
                          onChange={(e) => setNewGroup(prev => ({ ...prev, color: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="bg-blue-500">Blue</option>
                          <option value="bg-green-500">Green</option>
                          <option value="bg-purple-500">Purple</option>
                          <option value="bg-orange-500">Orange</option>
                          <option value="bg-red-500">Red</option>
                          <option value="bg-pink-500">Pink</option>
                        </select>
                      </div>
                      <div className="flex space-x-3 pt-4">
                        <button
                          type="button"
                          onClick={() => setShowCreateGroup(false)}
                          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                                                  <button
                            type="submit"
                            className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                          >
                            Create Category
                          </button>
                      </div>
                    </form>
                  </div>
                </div>
              </>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Search and Filters */}
            {!showCreateGroup && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
              <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search content..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <select
                    value={filters.sort_by}
                    onChange={(e) => handleFilterChange('sort_by', e.target.value)}
                    className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="latest">Latest</option>
                    <option value="popular">Most Popular</option>
                    <option value="trending">Trending</option>
                  </select>
                  
                  <button
                    type="submit"
                    className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Search
                  </button>
                </div>
              </form>
            </div>
            )}

            {/* Content Grid */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-6">
                                             <h3 className="text-xl font-semibold text-gray-900">
              All Public Content
              <span className="text-gray-500 font-normal ml-2">({filteredItems.length} items)</span>
            </h3>
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  {refreshing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  <span>Refresh</span>
                </button>
              </div>
              
              {filteredItems.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FolderOpen className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No content found</h3>
                  <p className="text-gray-500">Try adjusting your search or filters</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {filteredItems.map((item) => (
                    <div key={item.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300">
                      {/* Header */}
                      <div className="p-4 border-b border-gray-100">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getContentTypeColor(item.content_type)}`}>
                              {item.content_type?.toUpperCase() || 'TEXT'}
                            </span>
                            {/* Group/Category Badge */}
                            {item.group && (
                              <div className="flex items-center space-x-2">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                  {item.group}
                                </span>
                                {/* Edit button for item owner */}
                                {user && item.user_id === user.id && (
                                  <button
                                    onClick={() => handleEditCategory(item)}
                                    className="p-1 text-gray-400 hover:text-indigo-600 transition-colors"
                                    title="Edit category"
                                  >
                                    <Settings className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center space-x-1">
                            <button className="p-1.5 text-gray-400 hover:text-red-500 transition-colors">
                              <Heart className="w-4 h-4" />
                            </button>
                            <button className="p-1.5 text-gray-400 hover:text-blue-500 transition-colors">
                              <Bookmark className="w-4 h-4" />
                            </button>
                            <button className="p-1.5 text-gray-400 hover:text-green-500 transition-colors">
                              <Share2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        
                        {/* Author Info */}
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-semibold text-sm">
                              {item.author_name ? item.author_name.charAt(0).toUpperCase() : 'U'}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {item.author_name || 'Anonymous'}
                            </div>
                            <div className="flex items-center space-x-2 text-xs text-gray-500">
                              <Clock className="w-3 h-3" />
                              <span>{new Date(item.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Content */}
                      <div className="p-4">
                        {item.title && (
                          <h3 className="font-semibold text-gray-900 mb-3 line-clamp-2">
                            {item.title}
                          </h3>
                        )}
                        
                        {/* Source URL */}
                        {item.source_url && (
                          <div className="mb-3">
                            <a
                              href={item.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-indigo-600 hover:text-indigo-800 underline break-all"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {item.source_url}
                            </a>
                          </div>
                        )}
                        
                        <div className="text-sm text-gray-700 line-clamp-3">
                          {item.content_type === 'html' ? (
                            <div className="space-y-2">
                              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 min-h-[200px]">
                                <div 
                                  className="html-content"
                                  dangerouslySetInnerHTML={{ __html: item.content }}
                                />
                              </div>
                              <p className="text-xs text-gray-500 text-center">
                                HTML Content
                              </p>
                            </div>
                          ) : item.content_type === 'image' ? (
                            <div className="space-y-3">
                              <img 
                                src={item.content} 
                                alt={item.title || 'Shared image'}
                                className="w-full h-64 object-contain rounded-lg border border-gray-200 bg-gray-50"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                              />
                              <div 
                                className="hidden w-full h-64 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 text-sm"
                              >
                                <div className="text-center">
                                  <Eye className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                                  <p>Image not available</p>
                                </div>
                              </div>
                            </div>
                          ) : item.content_type === 'link' && item.content.match(/^https?:\/\//) ? (
                            <div className="space-y-3">
                              <div className="text-sm">
                                <a
                                  href={item.content}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 underline break-all"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {item.content}
                                </a>
                              </div>
                              <LinkPreview url={item.content} />
                            </div>
                          ) : (
                            item.content
                          )}
                        </div>
                        
                        {/* Tags */}
                        {item.tags && Array.isArray(item.tags) && item.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-3">
                            {item.tags.slice(0, 3).map((tag, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-indigo-100 text-indigo-800"
                              >
                                <Hash className="w-3 h-3 mr-1" />
                                {tag}
                              </span>
                            ))}
                            {item.tags.length > 3 && (
                              <span className="text-xs text-gray-500">
                                +{item.tags.length - 3} more
                              </span>
                            )}
                          </div>
                        )}

                        {/* Inline Category Editor */}
                        {editingItem === item.id && (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-center space-x-2">
                              <select
                                value={editCategory}
                                onChange={(e) => setEditCategory(e.target.value)}
                                className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                disabled={categoriesLoading}
                              >
                                <option value="">Select a category...</option>
                                {categories.map((category) => (
                                  <option key={category.id} value={category.id}>
                                    {category.name}
                                  </option>
                                ))}
                              </select>
                              <button
                                onClick={() => handleSaveCategory(item.id)}
                                disabled={!editCategory || categoriesLoading}
                                className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Save
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="px-3 py-1.5 bg-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-400 transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Footer */}
                      <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <div className="flex items-center space-x-4">
                            <span className="flex items-center space-x-1">
                              <Eye className="w-3 h-3" />
                              <span>{Math.floor(Math.random() * 100) + 10}</span>
                            </span>
                            <span className="flex items-center space-x-1">
                              <Heart className="w-3 h-3" />
                              <span>{Math.floor(Math.random() * 20) + 1}</span>
                            </span>
                            <span className="flex items-center space-x-1">
                              <MessageCircle className="w-3 h-3" />
                              <span>{Math.floor(Math.random() * 10)}</span>
                            </span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Award className="w-3 h-3" />
                            <span>Featured</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="flex items-center justify-center space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1}
                  className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                <span className="px-4 py-2 text-sm text-gray-700">
                  Page {pagination.current_page} of {pagination.total_pages}
                </span>
                
                <button
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={pagination.current_page === pagination.total_pages}
                  className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicPage5;